import {
	AfterViewInit,
	ChangeDetectionStrategy,
	Component,
	DestroyRef,
	EventEmitter,
	inject,
	Inject,
	Input,
	OnChanges,
	OnInit,
	Output,
	signal,
	SimpleChanges,
} from '@angular/core';
import {
	DateTimeFormats,
	TimeFormats,
	TrafficType,
	WhatsappButtonType,
	WhatsappHeaderType,
	WhatsappTemplateCategory,
	type WhatsappTemplateType,
} from '@campaign-portal/namespace/common/enums';
import { FileInfo } from '@campaign-portal/namespace/common/fileInfo';
import { convertToHTMLFormat } from '@helpers/utils/template-message.serializer';
import { ContactFieldsService } from '../../../contacts/services/contact-fields.service';
import {
	AbstractConfigService,
	AlarisFilesService,
	AlarisIconDirective,
	AlarisLanguageService,
	AlarisTooltipDirective,
	CONFIG_SERVICE_INJECTOR,
	formatBytes,
	pluralize,
	Typecast,
} from '@campaign-portal/components-library';
import { TranslateModule } from '@ngx-translate/core';
import { DatePipe } from '@angular/common';
import { LinkDomainsService } from '../../../url-shortener/link-domains.service';
import { CampaignShortLink } from '@campaign-portal/namespace/entities/campaigns/specs';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { WhatsappAuthentication, WhatsappHeader } from '@campaign-portal/namespace/entities/templates/specs';
import { CdkCopyToClipboard } from '@angular/cdk/clipboard';
import { BehaviorSubject, distinctUntilChanged, of, switchMap } from 'rxjs';
import { FilesCheckUrlRequest, FileUrlInfo } from '@campaign-portal/namespace/entities/files/specs';
import { CPCustomValidators } from '@helpers/validators/custom-validators';
import { UrlShortenerUtilsService } from '../../../url-shortener/url-shortener-utils.service';

export interface PhonePreviewWhatsappSettings {
	category?: WhatsappTemplateCategory;
	type?: WhatsappTemplateType | null;
	language?: string | null;
	authentication?: Partial<WhatsappAuthentication>;
	header?: Partial<WhatsappHeader>;
	footer?: string;
	buttons?: {
		type?: WhatsappButtonType | null;
		name?: string;
		phone?: string;
		url?: string;
		code?: string;
		shortUrl?: string;
	}[];
}

export enum WhatsappFileType {
	IMAGE = 'IMAGE',
	DOCUMENT = 'DOCUMENT',
}

export interface WhatsappFileUrlInfo extends FileUrlInfo {
	type: WhatsappFileType;
}

@Component({
	selector: 'app-phone-message',
	templateUrl: './phone-message.component.html',
	styleUrls: ['./phone-message.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [TranslateModule, DatePipe, AlarisIconDirective, CdkCopyToClipboard, AlarisTooltipDirective],
	standalone: true,
	providers: [DatePipe],
})
export class PhoneMessageComponent implements OnChanges, OnInit, AfterViewInit {
	// main message params
	@Input() trafficType!: TrafficType;
	@Input() sender = '';
	@Input() message = '';
	// omni message params
	@Input() buttonActionUrl?: string;
	@Input() imageUrl?: string;
	@Input() buttonCaption?: string;
	@Input() shortLink?: CampaignShortLink;
	// message from file params
	@Input() file?: FileInfo;
	// whatsapp
	@Input() whatsappSettings?: PhonePreviewWhatsappSettings;
	@Output() whatsappFileUrlInfoChange = new EventEmitter<WhatsappFileUrlInfo | null>();

	readonly formatBytes = formatBytes;
	readonly String = String;
	readonly TrafficType = TrafficType;
	readonly WhatsappHeaderType = WhatsappHeaderType;
	readonly WhatsappButtonType = WhatsappButtonType;
	readonly WhatsappTemplateCategory = WhatsappTemplateCategory;
	readonly WhatsappFileType = WhatsappFileType;
	readonly Infinity = Infinity;
	readonly today = new Date();

	readonly whatsappHeaderUrl$ = new BehaviorSubject<string | null>(null);
	readonly destroyRef = inject(DestroyRef);

	messageInnerHTML = signal<string>('');
	themeType = toSignal(this.appConfigService.themeType$);

	// whatsapp
	whatsappMaxVisibleButtons = signal<number>(2);
	whatsappAuthMessageInnerHTML = signal<string>('');
	whatsappHeaderInnerHTML = signal<string>('');
	whatsappFileUrlInfo = signal<WhatsappFileUrlInfo | null>(null);

	constructor(
		readonly shortenerUtils: UrlShortenerUtilsService,
		private readonly fs: AlarisFilesService,
		private readonly contactFieldsService: ContactFieldsService,
		private readonly domains: LinkDomainsService,
		private readonly ls: AlarisLanguageService,
		@Inject(CONFIG_SERVICE_INJECTOR) private readonly appConfigService: AbstractConfigService,
		private readonly datePipe: DatePipe
	) {}

	get isWhatsappAuthentication(): boolean {
		return (
			this.trafficType === TrafficType.WHATSAPP &&
			this.whatsappSettings?.category === WhatsappTemplateCategory.AUTHENTICATION
		);
	}

	get style(): string {
		return `background-image: url(assets/img/phone-${this.trafficType}-${this.themeType()}.webp)`;
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes.message) {
			this.convertMessageText();
		}
		if (changes.whatsappSettings) {
			this.convertWhatsappHeaderText();
			this.setWhatsappAuthMessage();
			this.checkWhatsappHeaderUrl();
		}
	}

	ngOnInit(): void {
		this.whatsappHeaderUrl$
			.pipe(
				distinctUntilChanged(),
				switchMap((url) => {
					const params: FilesCheckUrlRequest = {
						Data: { url: url ?? '' },
					};
					return url && CPCustomValidators.URL_REGEXP.test(url) ? this.fs.checkUrl(params) : of(null);
				}),
				takeUntilDestroyed(this.destroyRef)
			)
			.subscribe((resp) => {
				const responseFileInfo = resp?.Data?.at(0);
				const whatsappFileType = this.getWhatsappFileType(responseFileInfo?.mimeType);
				const fileInfo: WhatsappFileUrlInfo | null =
					responseFileInfo && whatsappFileType ? { type: whatsappFileType, ...responseFileInfo } : null;
				this.whatsappFileUrlInfo.set(fileInfo);
				this.whatsappFileUrlInfoChange.emit(fileInfo);
			});
	}

	ngAfterViewInit(): void {
		this.convertMessageText();
		this.convertWhatsappHeaderText();
		this.setWhatsappAuthMessage();
	}

	download(): void {
		if (this.file?.id) {
			this.fs.export(this.file.id, this.file.name).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
		}
	}

	setWhatsappAuthMessage(): void {
		const code = this.whatsappSettings?.authentication?.code;
		if (this.isWhatsappAuthentication) {
			const parsedCode = convertToHTMLFormat(
				code ?? '',
				this.trafficType,
				this.processVariables(code ?? '', this.contactFieldsService.variables$.getValue())
			);
			const message = parsedCode
				? this.ls.translate('templates.verificationCodeMessage', { code: parsedCode })
				: this.ls.translate('templates.verificationCodeEmptyMessage');
			this.whatsappAuthMessageInnerHTML.set(message);
		}
	}

	createWhatsappAuthFooter(): string {
		const codeExpirationDate = this.whatsappSettings?.authentication?.codeExpirationDate ?? '';
		const [hours, minutes, seconds] = codeExpirationDate.split(':').map(Number);
		if (!Typecast.isNumber(hours) || !Typecast.isNumber(minutes) || !Typecast.isNumber(seconds)) {
			return this.ls.translate('templates.codeExpiresDefaultFooter');
		}
		let time = '';
		if (hours) {
			const pluralized = pluralize(hours, ['agoPipe.hours.one', 'agoPipe.hours.few', 'agoPipe.hours.other']);
			time += hours + this.ls.translate(pluralized);
		}
		if (minutes) {
			const pluralized = pluralize(minutes, ['agoPipe.minutes.one', 'agoPipe.minutes.few', 'agoPipe.minutes.other']);
			time += minutes + this.ls.translate(pluralized);
		}
		if (seconds) {
			const pluralized = pluralize(seconds, ['agoPipe.seconds.one', 'agoPipe.seconds.few', 'agoPipe.seconds.other']);
			time += seconds + this.ls.translate(pluralized);
		}
		return this.ls.translate('templates.codeExpiresFooter', { time });
	}

	createWhatsappGeoHref(): string {
		const { type, value } = this.whatsappSettings?.header ?? {};
		if (type === WhatsappHeaderType.GEOLOCATION && value) {
			const iOS = /(iPad|iPhone|iPod)/g.test(navigator.userAgent);
			const query = encodeURIComponent(value);
			return iOS ? `http://maps.apple.com/?q=${query}` : `https://maps.google.com/?q=${query}`;
		}
		return '';
	}

	private processVariables(text: string, allowedVariableList: string[]): { fullMatch: string; appearance: string }[] {
		const regex = /%([^%:]+)(?::([^%]+))?%/g;
		const matches: { fullMatch: string; appearance: string }[] = [];
		let match: RegExpExecArray | null = null;

		while ((match = regex.exec(text)) !== null) {
			const variableName = match[1];
			const dateTimeFormat = match[2] || '';

			if (!allowedVariableList.includes(variableName)) {
				continue;
			}
			const transformDateFormat = (format: string): string => {
				return ([...DateTimeFormats, ...TimeFormats] as unknown as string[]).includes(format)
					? this.datePipe.transform(new Date(), format) || ''
					: format;
			};

			const appearance = dateTimeFormat ? `${variableName}: ${transformDateFormat(dateTimeFormat)}` : variableName;
			matches.push({
				fullMatch: match[0],
				appearance,
			});
		}

		return matches;
	}

	private convertMessageText(): void {
		const message = this.message
			? convertToHTMLFormat(
					this.message,
					this.trafficType,
					this.processVariables(this.message, this.contactFieldsService.variables$.getValue()),
					this.shortLink ? this.domains.list : this.domains.active$.value,
					this.shortLink
				)
			: this.ls.translate('templates.messageWillBeHere');
		this.messageInnerHTML.set(message);
	}

	private checkWhatsappHeaderUrl(): void {
		const { type, value } = this.whatsappSettings?.header ?? {};
		if (type === WhatsappHeaderType.URL) {
			this.whatsappHeaderUrl$.next(value ?? null);
		}
	}

	private convertWhatsappHeaderText(): void {
		const { type, value } = this.whatsappSettings?.header ?? {};
		if (value && type === WhatsappHeaderType.TEXT) {
			this.whatsappHeaderInnerHTML.set(
				convertToHTMLFormat(
					value,
					this.trafficType,
					this.processVariables(value, this.contactFieldsService.variables$.getValue())
				)
			);
		}
	}

	private getWhatsappFileType(mimeType?: string): WhatsappFileType | null {
		if (!mimeType) {
			return null;
		}

		// https://developers.facebook.com/docs/whatsapp/cloud-api/reference/media#supported-media-types
		const imageTypes = ['image/jpeg', 'image/png'];
		const documentTypes = [
			'text/plain',
			'application/vnd.ms-excel',
			'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
			'application/msword',
			'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
			'application/vnd.ms-powerpoint',
			'application/vnd.openxmlformats-officedocument.presentationml.presentation',
			'application/pdf',
		];
		const isSomeTypeSupported = (types: string[]): boolean => {
			return types.some((type) => mimeType.includes(type));
		};
		return isSomeTypeSupported(imageTypes)
			? WhatsappFileType.IMAGE
			: isSomeTypeSupported(documentTypes)
				? WhatsappFileType.DOCUMENT
				: null;
	}
}
